import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useTravelEsimQrCodeUrl,
  useTravelEsimQrCodeUrlById,
} from '@/hooks/useTravelEsimOrders';
import { Download, Loader2 } from 'lucide-react';

type TravelEsimQrCodeSource =
  /** The QR code attached to an order line (admin order detail). */
  | { orderId: string; itemId: string }
  /** The QR code carried by the eSIM itself (client pages). */
  | { esimId: string };

type TravelEsimQrCodeDialogProps = TravelEsimQrCodeSource & {
  title: string;
  onClose: () => void;
};

/** Shows the QR code of a travel eSIM from its short-lived signed URL. */
export function TravelEsimQrCodeDialog(props: TravelEsimQrCodeDialogProps) {
  const { title, onClose } = props;
  const byLine = useTravelEsimQrCodeUrl(
    'orderId' in props ? props.orderId : undefined,
    'orderId' in props ? props.itemId : undefined,
    'orderId' in props,
  );
  const byEsim = useTravelEsimQrCodeUrlById(
    'esimId' in props ? props.esimId : undefined,
    'esimId' in props,
  );
  const { data, isLoading, isError } = 'esimId' in props ? byEsim : byLine;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            QR code eSIM
          </DialogTitle>
          <p className="text-sm text-slate-500">{title}</p>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          ) : isError || !data ? (
            <p className="text-sm text-red-600">
              Impossible de charger le QR code.
            </p>
          ) : (
            <>
              <img
                src={data.url}
                alt="QR code eSIM"
                className="h-56 w-56 rounded-xl border border-slate-200 bg-white object-contain p-2"
              />
              <Button asChild variant="outline" className="rounded-full gap-2">
                <a href={data.url} target="_blank" rel="noreferrer" download>
                  <Download className="h-4 w-4" />
                  Télécharger
                </a>
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
